import React from "react";
import { Link } from "react-router-dom";
import { Mail } from "lucide-react";

const SUPPORT_EMAIL = "blake@sweeplaunch.com";
const LAST_UPDATED = "June 20, 2026";

const Section = ({ id, title, children }) => (
  <section id={id} className="scroll-mt-24" data-testid={`privacy-section-${id}`}>
    <h2 className="font-display text-2xl font-bold tracking-tight text-zinc-950 mt-12 mb-4">{title}</h2>
    <div className="space-y-4 text-[15px] leading-relaxed text-zinc-700">{children}</div>
  </section>
);

const TOC = [
  ["overview", "Overview"],
  ["merchant-data", "Shopify Merchant Data"],
  ["customer-data", "Customer Data"],
  ["giveaway-entries", "Giveaway Entries"],
  ["billing", "Billing Information"],
  ["cookies", "Cookies & Sessions"],
  ["analytics", "Analytics"],
  ["compliance", "GDPR & CCPA Compliance"],
  ["deletion", "Data Deletion Requests"],
  ["retention", "Data Retention"],
  ["contact", "Contact Us"],
];

export default function PrivacyPolicy() {
  return (
    <div className="min-h-screen bg-[#FAFAFA]">
      <header className="sticky top-0 z-50 bg-white/70 backdrop-blur-xl border-b border-zinc-200">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2" data-testid="privacy-home-link">
            <img src="/assets/app-icon-1200.png" alt="SweepLaunch" className="size-7 rounded-md object-cover" />
            <span className="font-display font-bold text-lg tracking-tight">SweepLaunch</span>
          </Link>
          <a href={`mailto:${SUPPORT_EMAIL}`} className="text-sm font-medium text-zinc-700 hover:text-zinc-950" data-testid="privacy-support-link">
            Contact support
          </a>
        </div>
      </header>

      <div className="max-w-3xl mx-auto px-6 py-16" data-testid="privacy-policy-root">
        <div className="text-[11px] uppercase tracking-[0.2em] text-zinc-500 font-semibold">Legal</div>
        <h1 className="font-display text-4xl sm:text-5xl font-bold tracking-tighter text-zinc-950 mt-2">Privacy Policy</h1>
        <p className="text-sm text-zinc-500 mt-3">Last updated: {LAST_UPDATED}</p>

        {/* Table of contents */}
        <nav className="mt-8 bg-white border border-zinc-200 rounded-md p-5">
          <div className="text-[11px] uppercase tracking-[0.2em] text-zinc-500 font-semibold mb-3">On this page</div>
          <ul className="grid sm:grid-cols-2 gap-x-6 gap-y-2 text-sm">
            {TOC.map(([id, label]) => (
              <li key={id}>
                <a href={`#${id}`} className="text-zinc-700 hover:text-zinc-950 hover:underline">{label}</a>
              </li>
            ))}
          </ul>
        </nav>

        <Section id="overview" title="Overview">
          <p>
            SweepLaunch (“we”, “us”, “our”) provides a sweepstakes and giveaway platform for Shopify merchants. This Privacy
            Policy explains what information we collect, how we use it, the legal bases for processing, and the rights you and
            your customers have. It applies to our website, our Shopify app, and all related services (collectively, the “Service”).
          </p>
          <p>
            By installing or using SweepLaunch, you agree to the practices described in this policy. If you do not agree, please
            do not use the Service.
          </p>
        </Section>

        <Section id="merchant-data" title="Shopify Merchant Data">
          <p>When you connect your Shopify store, we receive and store data needed to operate giveaways on your behalf:</p>
          <ul className="list-disc pl-5 space-y-1.5">
            <li>Your store domain (e.g. <span className="font-mono text-sm">your-store.myshopify.com</span>), store name, and contact email.</li>
            <li>An OAuth access token scoped to the permissions you approve (e.g. read orders, read customers) used to read order data and register webhooks.</li>
            <li>Order events delivered via Shopify’s <span className="font-mono text-sm">orders/create</span> webhook, used to convert qualifying purchases into giveaway entries.</li>
            <li>App subscription and billing status returned by Shopify’s Billing API.</li>
          </ul>
          <p>
            We access Shopify data only to provide the Service. We do not sell merchant data, and we do not use your store data
            to build advertising profiles.
          </p>
        </Section>

        <Section id="customer-data" title="Customer Data">
          <p>
            To run sweepstakes, we process limited personal information about your customers and giveaway participants,
            which may include:
          </p>
          <ul className="list-disc pl-5 space-y-1.5">
            <li>Name and email address.</li>
            <li>Purchase amount and order identifier (to calculate entries earned).</li>
            <li>IP address and basic device signals used for fraud detection.</li>
            <li>Referral relationships between participants (who referred whom).</li>
          </ul>
          <p>
            We act as a <strong>data processor</strong> on behalf of the merchant (the data controller). Customers should direct
            data requests to the merchant whose giveaway they entered; merchants can fulfill these requests using the tools and
            endpoints described below.
          </p>
        </Section>

        <Section id="giveaway-entries" title="Giveaway Entries">
          <p>
            Entries are records created when a customer participates in a sweepstakes — through a purchase, an Alternative Method
            of Entry (AMOE / no-purchase entry), a referral, or a bonus action. Each entry stores the participant’s email, the
            entry source, the number of entries earned, an associated purchase amount (if any), a fraud score, and a timestamp.
          </p>
          <p>
            Entry data is used to operate the giveaway: tallying entries, building leaderboards, detecting fraud, drawing winners,
            and generating compliance records (such as AMOE confirmations and official rules). Winner selection uses a weighted
            random draw and excludes entries flagged as fraudulent.
          </p>
        </Section>

        <Section id="billing" title="Billing Information">
          <p>
            Paid plans are billed exclusively through <strong>Shopify Billing</strong>. Charges appear on your regular Shopify
            invoice. We do <strong>not</strong> collect, store, or process your credit card or bank details — payment credentials
            are handled entirely by Shopify.
          </p>
          <p>
            We store your selected plan, subscription status, and the Shopify subscription identifier so we can grant the correct
            features and reconcile your subscription state with Shopify.
          </p>
        </Section>

        <Section id="cookies" title="Cookies & Sessions">
          <p>
            We use a single, essential session cookie (<span className="font-mono text-sm">session_token</span>) to keep merchants
            securely signed in. It is strictly necessary for the Service to function and is not used for advertising or
            cross-site tracking.
          </p>
          <p>
            Authentication for merchants is provided via Google sign-in. We receive your name, email address, and profile picture
            from Google to create and identify your account. We do not receive your Google password.
          </p>
        </Section>

        <Section id="analytics" title="Analytics">
          <p>
            We collect aggregated, operational analytics about giveaway performance (entries, tracked revenue, conversion, and
            health metrics) to power dashboards and the “Giveaway Success Score.” These metrics are derived from your own store
            and entry data and are shown back to you inside the app.
          </p>
          <p>
            If you connect an optional third-party integration (such as an email marketing provider), data you choose to share is
            governed by that provider’s privacy policy in addition to this one.
          </p>
        </Section>

        <Section id="compliance" title="GDPR & CCPA Compliance">
          <p>
            We support compliance with the EU General Data Protection Regulation (GDPR), the UK GDPR, and the California Consumer
            Privacy Act (CCPA/CPRA).
          </p>
          <p><strong>Your rights</strong> may include the right to access, correct, port, restrict, or delete your personal data,
            and the right to object to or withdraw consent for certain processing. California residents have the right to know what
            personal information is collected and to request deletion. We do not “sell” or “share” personal information as those
            terms are defined under the CCPA.</p>
          <p>
            <strong>Lawful bases</strong> for processing include performing our contract with merchants, complying with legal
            obligations, and our legitimate interests in operating, securing, and improving the Service.
          </p>
          <p>
            As a Shopify app, we implement Shopify’s mandatory compliance webhooks — <span className="font-mono text-sm">customers/data_request</span>,
            <span className="font-mono text-sm"> customers/redact</span>, and <span className="font-mono text-sm">shop/redact</span> —
            so that data access and erasure requests routed through Shopify are honored automatically.
          </p>
        </Section>

        <Section id="deletion" title="Data Deletion Requests">
          <p>You can request deletion of personal data in any of the following ways:</p>
          <ul className="list-disc pl-5 space-y-1.5">
            <li>
              <strong>Customers:</strong> contact the merchant whose giveaway you entered, or submit a request through your Shopify
              order. Verified requests trigger Shopify’s <span className="font-mono text-sm">customers/redact</span> webhook, and we
              delete that customer’s entry records.
            </li>
            <li>
              <strong>Merchants:</strong> uninstalling the SweepLaunch app revokes our access and disables your account. 48 hours
              after uninstall, Shopify sends the <span className="font-mono text-sm">shop/redact</span> webhook, upon which we
              permanently delete your store’s giveaways, entries, referral records, and related data.
            </li>
            <li>
              <strong>Anyone:</strong> email us at <a href={`mailto:${SUPPORT_EMAIL}`} className="text-zinc-950 underline">{SUPPORT_EMAIL}</a> and
              we will process your request within 30 days.
            </li>
          </ul>
        </Section>

        <Section id="retention" title="Data Retention">
          <p>
            We retain merchant and entry data for as long as your account is active and a giveaway is running, plus a reasonable
            period afterward to meet legal, compliance, and dispute-resolution obligations. When a deletion request or
            <span className="font-mono text-sm"> shop/redact</span> event is received, we remove the associated data from our active
            systems. We may retain limited records where required by law.
          </p>
        </Section>

        <Section id="contact" title="Contact Us">
          <p>
            Questions, concerns, or privacy requests? We’re here to help.
          </p>
          <div className="bg-white border border-zinc-200 rounded-md p-5 flex items-center gap-3" data-testid="privacy-contact-card">
            <div className="size-10 rounded-md bg-zinc-950 flex items-center justify-center">
              <Mail className="size-5 text-white" strokeWidth={1.5} />
            </div>
            <div>
              <div className="text-[11px] uppercase tracking-[0.2em] text-zinc-500 font-semibold">Support & Privacy</div>
              <a href={`mailto:${SUPPORT_EMAIL}`} className="font-display font-bold text-zinc-950 hover:underline" data-testid="privacy-contact-email">{SUPPORT_EMAIL}</a>
            </div>
          </div>
          <p className="text-sm text-zinc-500">
            We may update this Privacy Policy from time to time. Material changes will be reflected by the “Last updated” date above.
          </p>
        </Section>

        <div className="mt-16 pt-8 border-t border-zinc-200 flex items-center justify-between text-sm text-zinc-500">
          <span>© {new Date().getFullYear()} SweepLaunch</span>
          <Link to="/" className="hover:text-zinc-950" data-testid="privacy-back-home">← Back to home</Link>
        </div>
      </div>
    </div>
  );
}
